/**
 * FINANCE REPORT VIEWER
 * Custom report viewer with beautiful formatting for financial reports
 * Uses the same data source as SharedReportViewer but with rich custom renderers
 */

'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  Play,
  Loader2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  ChevronDown,
  FileBarChart,
  Mail,
  Bell,
  Clock,
  Send,
  CheckCircle,
  XCircle,
  Package,
  Warehouse,
  ArrowUpRight,
  Wallet,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { formatTimeSAST } from '@/lib/sastTime';
import type { ReportDefinition } from '@/types/reports/shared-reports';
import { FINANCE_REPORT_ACTIONS } from '@/lib/shared/financeReportDefinitions';
import type { DocumentType } from '@/lib/reports';
import { formatUnifiedCurrency } from '@/lib/reports/currencyFormat';
import { DatePickerInput } from '@/components/shared/DateTimeInput';
import { emitGuidedTourTrainingEvent } from '@/components/training/guided-tour';

interface FinanceReportViewerProps {
  report: ReportDefinition;
  onBack: () => void;
  initialParameters?: Record<string, any>;
  autoExecute?: boolean;
  moduleColor?: string;
  onPreviewContextChange?: (context: {
    documentType: DocumentType;
    reportData: any;
    period?: { from: string; to: string };
  } | null) => void;
}

type DynamicOption = {
  value: string;
  label: string;
};

function isParameterSatisfied(param: ReportDefinition['parameters'][number], value: any): boolean {
  if (!param.required) {
    return true;
  }

  if (param.type === 'date_range') {
    return Boolean(value?.start && value?.end);
  }

  if (param.type === 'date') {
    return Boolean(value);
  }

  if (param.type === 'multiselect') {
    const selected = Array.isArray(value) ? value.filter(Boolean) : [];
    return selected.length > 0;
  }

  return value !== undefined && value !== null && value !== '';
}

function formatDateForRequest(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }

  // Preserve YYYY-MM-DD strings to avoid timezone shifts during new Date() parsing.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const date = new Date(value as string | number | Date);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date.toISOString().split('T')[0];
}

function toDateInputValue(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = new Date(value as string | number | Date);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
}

function buildParameterPayload(param: ReportDefinition['parameters'][number], value: any) {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  switch (param.type) {
    case 'date_range': {
      const start = formatDateForRequest(value?.start);
      const end = formatDateForRequest(value?.end);
      if (!start && !end) {
        return null;
      }
      return {
        date_from: start,
        date_to: end,
      };
    }
    case 'date': {
      const formatted = formatDateForRequest(value);
      return formatted ? { [param.name]: formatted } : null;
    }
    case 'multiselect': {
      const normalized = Array.isArray(value) ? value.filter(Boolean) : [];
      return normalized.length > 0 ? { [param.name]: normalized } : null;
    }
    case 'number': {
      const numericValue = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(numericValue) ? { [param.name]: numericValue } : { [param.name]: value };
    }
    case 'boolean':
      return { [param.name]: Boolean(value) };
    default:
      return { [param.name]: value };
  }
}

/** Heuristic for generic finance report tables — treat money-like numeric columns as currency */
function genericFieldKeyLooksLikeMoney(key: string): boolean {
  const k = key.toLowerCase().replace(/^[_]+/, '');
  if (
    k.endsWith('_count')
    || k === 'count'
    || k.includes('quantity')
    || k.includes('_qty')
    || k.endsWith('_qty')
    || k.includes('percent')
    || k.includes('percentage')
    || k.includes('margin')
    || k.includes('_rate')
    || k.endsWith('rate')
    || k.includes('days_')
    || k.endsWith('_days')
    || k.includes('frequency')
    || k.includes('_id')
    || k.endsWith('_id')
  ) {
    return false;
  }
  return (
    k.includes('amount')
    || k.includes('balance')
    || k.includes('revenue')
    || k.includes('price')
    || k.includes('_cost')
    || k.endsWith('cost')
    || k.includes('value')
    || k.includes('paid')
    || k.includes('payable')
    || k.includes('receivable')
    || k.includes('debit')
    || k.includes('credit')
    || k.includes('profit')
    || k.includes('expense')
    || k.includes('tax')
    || k.includes('_fee')
    || k.endsWith('fee')
    || k.includes('outstanding')
    || k.includes('invoice')
    || k.includes('subtotal')
    || k.endsWith('_total')
    || /(^|_)total$/i.test(k)
    || k.includes('duty')
    || k.includes('discount')
    || k.includes('opening')
    || k.includes('closing')
    || k.includes('_sales')
    || k.endsWith('sales')
    || k.includes('_purchases')
    || k.endsWith('purchases')
    || (/^sales$/i.test(k) || /^purchases$/i.test(k))
  );
}

const formatQuantity = (value: number | string | null | undefined): string => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '0';

  return numericValue.toLocaleString('en-ZA', {
    minimumFractionDigits: Number.isInteger(numericValue) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const toNumber = (value: unknown): number => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatPercent = (value: number | string | null | undefined): string => {
  const numericValue = toNumber(value);
  return `${numericValue.toFixed(1)}%`;
};

const normalizeCreditTerms = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  return '30 Days';
};

const hasPositiveCreditLimit = (value: unknown): boolean => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0;
};

const getCreditTermsBadgeClasses = (creditTerms: string): string => {
  const normalized = creditTerms.trim().toLowerCase();

  if (normalized.includes('cod') || normalized.includes('prepaid')) {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }

  if (normalized.includes('60') || normalized.includes('90') || normalized.includes('consignment')) {
    return 'bg-rose-50 text-rose-700 border-rose-200';
  }

  if (normalized.includes('30')) {
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }

  return 'bg-slate-50 text-slate-700 border-slate-200';
};

function getFinanceDocumentType(reportId: string): DocumentType {
  return reportId.replace(/^finance_/, '') as DocumentType;
}

function derivePreviewPeriod(reportData: any, parameters: Record<string, any>): { from: string; to: string } | undefined {
  if (reportData?.period?.from && reportData?.period?.to) {
    return {
      from: String(reportData.period.from),
      to: String(reportData.period.to),
    };
  }

  if (reportData?.as_of_date) {
    const asOfDate = String(reportData.as_of_date);
    return {
      from: asOfDate,
      to: asOfDate,
    };
  }

  const dateRange = parameters.date_range;
  if (dateRange?.start && dateRange?.end) {
    const from = formatDateForRequest(dateRange.start);
    const to = formatDateForRequest(dateRange.end);

    if (from && to) {
      return { from, to };
    }
  }

  const from = formatDateForRequest(parameters.date_from || parameters.from_date || parameters.start_date);
  const to = formatDateForRequest(parameters.date_to || parameters.to_date || parameters.end_date);

  if (from && to) {
    return { from, to };
  }

  if (from) {
    return { from, to: from };
  }

  if (to) {
    return { from: to, to };
  }

  return undefined;
}

export default function FinanceReportViewer({ report, onBack, initialParameters, autoExecute = false, moduleColor, onPreviewContextChange }: FinanceReportViewerProps) {
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [baseCurrencyCode, setBaseCurrencyCode] = useState('ZAR');

  const formatCurrency = useCallback(
    (value: number | null | undefined) =>
      formatUnifiedCurrency(value ?? 0, baseCurrencyCode),
    [baseCurrencyCode],
  );

  // Tenant base currency — matches PDF/unified adapters (work_item_settings.base_currency)

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        return;
      }

      try {
        const res = await fetch('/api/v1/finance/settings', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok || cancelled) {
          return;
        }

        const json = await res.json();
        const code = json?.data?.base_currency;

        if (typeof code === 'string' && code.trim() && !cancelled) {
          setBaseCurrencyCode(code.trim().toUpperCase());
        }
      } catch {
        /* keep default */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Theme colors for header
  const { clientBranding, headerColor, isDark } = useTheme();
  const primaryColor = moduleColor || clientBranding?.primary_color || '#3B82F6';
  const _headerBgColor = moduleColor || headerColor || clientBranding?.header_color || primaryColor;

  // Dynamic options for select fields
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, DynamicOption[]>>({});
  const autoExecutedKeyRef = useRef<string | null>(null);

  // Load dynamic options for select parameters
  useEffect(() => {
    let isCancelled = false;

    const loadDynamicOptions = async () => {
      const token = authService.getTokens()?.access_token;
      setDynamicOptions({});

      if (!token) return;

      const dynamicSelectParams = report.parameters.filter(
        (param) => param.type === 'select' && (!param.options || param.options.length === 0)
      );

      if (dynamicSelectParams.length === 0) {
        return;
      }

      const loadedEntries = await Promise.all(
        dynamicSelectParams.map(async (param): Promise<[string, DynamicOption[]] | null> => {
          try {
            if (param.name === 'contact_id') {
              const response = await fetch('/api/v1/finance', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'getContacts', contact_type: 'customer' })
              });

              if (!response.ok) {
                return null;
              }

              const data = await response.json();
              const contacts = data.data || [];

              return [
                'contact_id',
                contacts.map((c: any) => ({
                  value: String(c.id),
                  label: c.company_name || c.contact_person || `Contact ${c.id}`
                }))
              ];
            }

            if (param.name === 'account_id') {
              const response = await fetch('/api/v1/finance', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'getChartOfAccounts' })
              });

              if (!response.ok) {
                return null;
              }

              const data = await response.json();
              const accounts = data.data || [];

              return [
                'account_id',
                accounts.map((a: any) => ({
                  value: String(a.id),
                  label: `${a.account_code || ''} - ${a.account_name}`.trim()
                }))
              ];
            }

            if (param.name === 'item_id') {
              const response = await fetch('/api/v1/finance', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'getItems', limit: 500 })
              });

              if (!response.ok) {
                return null;
              }

              const data = await response.json();
              const items = (data.data || []).filter((item: any) => item.item_type !== 'service');

              return [
                'item_id',
                items.map((item: any) => ({
                  value: String(item.id),
                  label: item.sku ? `${item.title || item.name} (${item.sku})` : (item.title || item.name || `Item ${item.id}`)
                }))
              ];
            }

            if (param.name === 'warehouse_id') {
              const response = await fetch('/api/v1/work-items/inventory/warehouses', {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });

              if (!response.ok) {
                return null;
              }

              const data = await response.json();
              const warehouses = data.data || [];

              return [
                'warehouse_id',
                warehouses.map((warehouse: any) => ({
                  value: String(warehouse.id),
                  label: warehouse.code ? `${warehouse.name} (${warehouse.code})` : (warehouse.name || `Warehouse ${warehouse.id}`)
                }))
              ];
            }

            if (param.name === 'category_id') {
              const response = await fetch('/api/v1/work-items/inventory/categories?applies_to=product', {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });

              if (!response.ok) {
                return null;
              }

              const data = await response.json();
              const categories = data.data || [];

              return [
                'category_id',
                categories.map((category: any) => ({
                  value: String(category.id),
                  label: category.category_name || category.name || `Category ${category.id}`
                }))
              ];
            }

            if (param.name === 'bank_account_id') {
              const response = await fetch('/api/v1/bank?resource=accounts', {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });

              if (!response.ok) {
                return null;
              }

              const data = await response.json();
              const accounts = data.data || [];

              return [
                'bank_account_id',
                [
                  { value: '', label: 'All bank accounts' },
                  ...accounts.map((a: any) => ({
                    value: String(a.id),
                    label: `${a.bank_name || 'Bank'} · ${a.account_name || 'Account'}`
                  })),
                ],
              ];
            }
          } catch (err) {
            console.error(`Failed to load dynamic options for ${param.name}:`, err);
          }

          return null;
        })
      );

      if (isCancelled) {
        return;
      }

      const nextOptions = loadedEntries.reduce<Record<string, DynamicOption[]>>((acc, entry) => {
        if (entry) {
          const [name, options] = entry;
          acc[name] = options;
        }
        return acc;
      }, {});

      setDynamicOptions(nextOptions);
    };

    void loadDynamicOptions();

    return () => {
      isCancelled = true;
    };
  }, [report]);

  // Initialize parameters with default values
  useEffect(() => {
    const defaultParams: Record<string, any> = {};
    report.parameters.forEach(param => {
      if (param.defaultValue !== undefined) {
        defaultParams[param.name] = param.defaultValue;
      }
    });
    setParameters({
      ...defaultParams,
      ...(initialParameters || {}),
    });
    setReportData(null);
    setError(null);
    autoExecutedKeyRef.current = null;
  }, [initialParameters, report]);

  const handleParameterChange = (paramName: string, value: any) => {
    setParameters(prev => ({ ...prev, [paramName]: value }));
  };

  const executeReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      // Get the action for this report
      const action = FINANCE_REPORT_ACTIONS[report.id];
      if (!action) {
        throw new Error(`Unknown report: ${report.id}`);
      }

      const body: any = { action };

      report.parameters.forEach((param) => {
        const payload = buildParameterPayload(param, parameters[param.name]);
        if (payload) {
          Object.assign(body, payload);
        }
      });

      const response = await fetch('/api/v1/finance-reports', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to execute report (${response.status})`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to execute report');
      }

      setReportData(result.data);
      emitGuidedTourTrainingEvent({ type: 'report_generate' });
    } catch (err: any) {
      setError(err.message);
      console.error('Report execution error:', err);
    } finally {
      setLoading(false);
    }
  }, [parameters, report]);

  const hasRequiredParameters = useMemo(() => {
    return report.parameters.every((param) => isParameterSatisfied(param, parameters[param.name]));
  }, [parameters, report.parameters]);

  const autoExecuteKey = useMemo(() => {
    if (!autoExecute) {
      return null;
    }

    return JSON.stringify({ reportId: report.id, parameters });
  }, [autoExecute, parameters, report.id]);

  useEffect(() => {
    if (!autoExecute || !autoExecuteKey || !hasRequiredParameters) {
      return;
    }

    if (autoExecutedKeyRef.current === autoExecuteKey || loading) {
      return;
    }

    autoExecutedKeyRef.current = autoExecuteKey;
    void executeReport();
  }, [autoExecute, autoExecuteKey, executeReport, hasRequiredParameters, loading]);

  useEffect(() => {
    if (!onPreviewContextChange) {
      return;
    }

    if (!reportData) {
      onPreviewContextChange(null);
      return;
    }

    onPreviewContextChange({
      documentType: getFinanceDocumentType(report.id),
      reportData,
      period: derivePreviewPeriod(reportData, parameters),
    });
  }, [onPreviewContextChange, parameters, report.id, reportData]);

  const renderParameterInput = (param: any) => {
    const value = parameters[param.name];

    switch (param.type) {
      case 'date':
        return (
          <DatePickerInput
            label={param.label}
            value={toDateInputValue(value)}
            onChange={(next) => handleParameterChange(param.name, next)}
            isDark={isDark}
            required={param.required}
            className="w-full"
          />
        );

      case 'date_range':
        return (
          <div className="grid grid-cols-2 gap-3">
            <DatePickerInput
              label="From Date"
              value={toDateInputValue(value?.start)}
              onChange={(next) => handleParameterChange(param.name, { ...value, start: next })}
              max={toDateInputValue(value?.end)}
              isDark={isDark}
              required={param.required}
              className="w-full"
            />
            <DatePickerInput
              label="To Date"
              value={toDateInputValue(value?.end)}
              onChange={(next) => handleParameterChange(param.name, { ...value, end: next })}
              min={toDateInputValue(value?.start)}
              isDark={isDark}
              required={param.required}
              className="w-full"
            />
          </div>
        );

      case 'select':
        const selectOptions = dynamicOptions[param.name] || param.options || [];
        return (
          <select
            value={value || ''}
            onChange={(e) => handleParameterChange(param.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required={param.required}
          >
            <option value="">Select...</option>
            {selectOptions.map((opt: any) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'multiselect': {
        const multiOptions = dynamicOptions[param.name] || param.options || [];
        const selectedValues = Array.isArray(value) ? value.map(String) : [];

        return (
          <div className="space-y-1.5">
            <select
              multiple
              value={selectedValues}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map((option) => option.value);
                handleParameterChange(param.name, selected);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px]"
              required={param.required}
            >
              {multiOptions.map((opt: any) => (
                <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400">Use Ctrl/Cmd + click to select multiple options.</p>
          </div>
        );
      }

      case 'number':
        return (
          <input
            type="number"
            value={value ?? ''}
            onChange={(e) => handleParameterChange(param.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required={param.required}
          />
        );

      case 'boolean':
        return (
          <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleParameterChange(param.name, e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Enabled</span>
          </label>
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleParameterChange(param.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required={param.required}
          />
        );
    }
  };

  // ============================================================================
  // CUSTOM REPORT RENDERERS - Beautiful formatting per report type
  // ============================================================================

  const renderProfitLoss = () => {
    if (!reportData) return null;

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Profit & Loss Statement</h3>
          <p className="text-sm text-gray-600">
            {reportData.period?.from} - {reportData.period?.to}
          </p>
        </div>

        {/* Income Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 text-lg border-b pb-2">Income</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Sales Revenue</span>
              <span>{formatCurrency(reportData.income?.sales_revenue)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Service Revenue</span>
              <span>{formatCurrency(reportData.income?.service_revenue)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Other Income</span>
              <span>{formatCurrency(reportData.income?.other_income)}</span>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
              <span>Total Income</span>
              <span className="text-green-600">{formatCurrency(reportData.income?.total_income)}</span>
            </div>
          </div>
        </div>

        {/* Cost of Sales Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 text-lg border-b pb-2">Cost of Sales</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Direct Costs</span>
              <span>{formatCurrency(reportData.cost_of_sales?.direct_costs)}</span>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
              <span>Total Cost of Sales</span>
              <span className="text-red-600">{formatCurrency(reportData.cost_of_sales?.total_cost_of_sales)}</span>
            </div>
          </div>
        </div>

        {/* Gross Profit */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
          <div className="flex justify-between items-center">
            <span className="font-bold text-lg">Gross Profit</span>
            <div className="text-right">
              <span className="text-2xl font-bold text-blue-600">{formatCurrency(reportData.gross_profit)}</span>
              <p className="text-sm text-gray-600">
                Margin: {((reportData.gross_profit_margin || 0) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        {/* Expenses Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 text-lg border-b pb-2">Operating Expenses</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Operating Expenses</span>
              <span>{formatCurrency(reportData.expenses?.operating_expenses)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Admin Expenses</span>
              <span>{formatCurrency(reportData.expenses?.admin_expenses)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Selling Expenses</span>
              <span>{formatCurrency(reportData.expenses?.selling_expenses)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Depreciation</span>
              <span>{formatCurrency(reportData.expenses?.depreciation)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Interest Expense</span>
              <span>{formatCurrency(reportData.expenses?.interest_expense)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Other Expenses</span>
              <span>{formatCurrency(reportData.expenses?.other_expenses)}</span>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
              <span>Total Expenses</span>
              <span className="text-red-600">{formatCurrency(reportData.expenses?.total_expenses)}</span>
            </div>
          </div>
        </div>

        {/* Net Profit */}
        <div className={`rounded-lg border p-6 ${reportData.net_profit >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Net Profit Before Tax</span>
              <span>{formatCurrency(reportData.net_profit_before_tax)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tax Expense (28%)</span>
              <span>{formatCurrency(reportData.tax_expense)}</span>
            </div>
            <div className="flex justify-between items-center font-bold text-lg pt-2 border-t mt-2">
              <span>Net Profit After Tax</span>
              <div className="text-right">
                <span className={`text-2xl ${reportData.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportData.net_profit)}
                </span>
                <p className="text-sm text-gray-600 font-normal">
                  Margin: {((reportData.net_profit_margin || 0) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBalanceSheet = () => {
    if (!reportData) return null;

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Balance Sheet</h3>
          <p className="text-sm text-gray-600">As of {reportData.as_of_date}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Assets */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4 text-lg border-b pb-2">ASSETS</h4>

            <div className="space-y-4">
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Current Assets</h5>
                <div className="space-y-1 pl-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Cash & Equivalents</span>
                    <span>{formatCurrency(reportData.assets?.current_assets?.cash_and_equivalents)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Accounts Receivable</span>
                    <span>{formatCurrency(reportData.assets?.current_assets?.accounts_receivable)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Inventory</span>
                    <span>{formatCurrency(reportData.assets?.current_assets?.inventory)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Prepaid Expenses</span>
                    <span>{formatCurrency(reportData.assets?.current_assets?.prepaid_expenses)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium border-t pt-1 mt-1">
                    <span>Total Current Assets</span>
                    <span className="text-blue-600">{formatCurrency(reportData.assets?.current_assets?.total_current_assets)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-gray-700 mb-2">Fixed Assets</h5>
                <div className="space-y-1 pl-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Property, Plant & Equipment</span>
                    <span>{formatCurrency(reportData.assets?.fixed_assets?.property_plant_equipment)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Less: Accumulated Depreciation</span>
                    <span className="text-red-600">{formatCurrency(reportData.assets?.fixed_assets?.accumulated_depreciation)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium border-t pt-1 mt-1">
                    <span>Net Fixed Assets</span>
                    <span className="text-blue-600">{formatCurrency(reportData.assets?.fixed_assets?.net_fixed_assets)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between text-base font-bold border-t-2 border-gray-300 pt-2 mt-4">
                <span>TOTAL ASSETS</span>
                <span className="text-green-600">{formatCurrency(reportData.assets?.total_assets)}</span>
              </div>
            </div>
          </div>

          {/* Liabilities & Equity */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-semibold text-gray-900 mb-4 text-lg border-b pb-2">LIABILITIES & EQUITY</h4>

            <div className="space-y-4">
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Current Liabilities</h5>
                <div className="space-y-1 pl-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Accounts Payable</span>
                    <span>{formatCurrency(reportData.liabilities?.current_liabilities?.accounts_payable)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Short-term Debt</span>
                    <span>{formatCurrency(reportData.liabilities?.current_liabilities?.short_term_debt)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Accrued Expenses</span>
                    <span>{formatCurrency(reportData.liabilities?.current_liabilities?.accrued_expenses)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium border-t pt-1 mt-1">
                    <span>Total Current Liabilities</span>
                    <span className="text-red-600">{formatCurrency(reportData.liabilities?.current_liabilities?.total_current_liabilities)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-gray-700 mb-2">Long-term Liabilities</h5>
                <div className="space-y-1 pl-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Long-term Debt</span>
                    <span>{formatCurrency(reportData.liabilities?.long_term_liabilities?.long_term_debt)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium border-t pt-1 mt-1">
                    <span>Total Long-term Liabilities</span>
                    <span className="text-red-600">{formatCurrency(reportData.liabilities?.long_term_liabilities?.total_long_term_liabilities)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between text-sm font-medium border-t pt-2 mt-2">
                <span>Total Liabilities</span>
                <span className="text-red-600">{formatCurrency(reportData.liabilities?.total_liabilities)}</span>
              </div>

              <div>
                <h5 className="font-medium text-gray-700 mb-2 mt-4">Equity</h5>
                <div className="space-y-1 pl-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Share Capital</span>
                    <span>{formatCurrency(reportData.equity?.share_capital)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Retained Earnings</span>
                    <span>{formatCurrency(reportData.equity?.retained_earnings)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current Year Earnings</span>
                    <span className={reportData.equity?.current_year_earnings >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(reportData.equity?.current_year_earnings)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-medium border-t pt-1 mt-1">
                    <span>Total Equity</span>
                    <span className="text-blue-600">{formatCurrency(reportData.equity?.total_equity)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between text-base font-bold border-t-2 border-gray-300 pt-2 mt-4">
                <span>TOTAL LIABILITIES & EQUITY</span>
                <span className="text-green-600">{formatCurrency(reportData.total_liabilities_and_equity)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAgedReceivables = () => {
    if (!reportData) return null;
    const customers = reportData.customers || [];
    const creditTermsPresent = customers.some((customer: any) => Boolean(customer.credit_terms));
    const creditLimitPresent = customers.some((customer: any) => hasPositiveCreditLimit(customer.credit_limit));

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Aged Receivables Report</h3>
          <p className="text-sm text-gray-600">As of {reportData.as_of_date}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600">Current</p>
            <p className="text-xl font-bold text-green-700">{formatCurrency(reportData.totals?.current)}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <p className="text-sm text-yellow-600">31-60 Days</p>
            <p className="text-xl font-bold text-yellow-700">{formatCurrency(reportData.totals?.days_31_60)}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-orange-600">61-90 Days</p>
            <p className="text-xl font-bold text-orange-700">{formatCurrency(reportData.totals?.days_61_90)}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-sm text-red-600">Over 90 Days</p>
            <p className="text-xl font-bold text-red-700">{formatCurrency(reportData.totals?.days_over_90)}</p>
          </div>
        </div>

        {/* Total Outstanding */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
          <p className="text-sm text-blue-600">Total Outstanding</p>
          <p className="text-3xl font-bold text-blue-700">{formatCurrency(reportData.totals?.total)}</p>
        </div>

        {/* Customer Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  {creditTermsPresent && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credit Terms</th>}
                  {creditLimitPresent && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit Limit</th>}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">1-30 Days</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">31-60 Days</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">61-90 Days</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Over 90 Days</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customers.map((customer: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{customer.customer_name}</td>
                    {creditTermsPresent && (
                      <td className="px-6 py-4 text-sm whitespace-nowrap">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${getCreditTermsBadgeClasses(normalizeCreditTerms(customer.credit_terms))}`}
                        >
                          {normalizeCreditTerms(customer.credit_terms)}
                        </span>
                      </td>
                    )}
                    {creditLimitPresent && (
                      <td className="px-6 py-4 text-sm text-right text-gray-900 whitespace-nowrap">
                        {hasPositiveCreditLimit(customer.credit_limit) ? formatCurrency(customer.credit_limit) : '—'}
                      </td>
                    )}
                    <td className="px-6 py-4 text-sm text-gray-900 text-right">{formatCurrency(customer.current)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right">{formatCurrency(customer.days_1_30)}</td>
                    <td className="px-6 py-4 text-sm text-yellow-600 text-right">{formatCurrency(customer.days_31_60)}</td>
                    <td className="px-6 py-4 text-sm text-orange-600 text-right">{formatCurrency(customer.days_61_90)}</td>
                    <td className="px-6 py-4 text-sm text-red-600 text-right font-semibold">{formatCurrency(customer.days_over_90)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right font-bold">{formatCurrency(customer.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderAgedPayables = () => {
    if (!reportData) return null;

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Aged Payables Report</h3>
          <p className="text-sm text-gray-600">As of {reportData.as_of_date}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600">Current</p>
            <p className="text-xl font-bold text-green-700">{formatCurrency(reportData.totals?.current)}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <p className="text-sm text-yellow-600">31-60 Days</p>
            <p className="text-xl font-bold text-yellow-700">{formatCurrency(reportData.totals?.days_31_60)}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-orange-600">61-90 Days</p>
            <p className="text-xl font-bold text-orange-700">{formatCurrency(reportData.totals?.days_61_90)}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-sm text-red-600">Over 90 Days</p>
            <p className="text-xl font-bold text-red-700">{formatCurrency(reportData.totals?.days_over_90)}</p>
          </div>
        </div>

        {/* Total Payable */}
        <div className="bg-red-50 rounded-lg p-4 border border-red-200 text-center">
          <p className="text-sm text-red-600">Total Payable</p>
          <p className="text-3xl font-bold text-red-700">{formatCurrency(reportData.totals?.total)}</p>
        </div>

        {/* Supplier Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">1-30 Days</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">31-60 Days</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">61-90 Days</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Over 90 Days</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reportData.suppliers?.map((supplier: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{supplier.supplier_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right">{formatCurrency(supplier.current)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right">{formatCurrency(supplier.days_1_30)}</td>
                    <td className="px-6 py-4 text-sm text-yellow-600 text-right">{formatCurrency(supplier.days_31_60)}</td>
                    <td className="px-6 py-4 text-sm text-orange-600 text-right">{formatCurrency(supplier.days_61_90)}</td>
                    <td className="px-6 py-4 text-sm text-red-600 text-right font-semibold">{formatCurrency(supplier.days_over_90)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right font-bold">{formatCurrency(supplier.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerStatement = () => {
    if (!reportData) return null;

    const summary = reportData.summary || {};
    const customer = reportData.customer || {};
    const transactions = reportData.transactions || [];

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Customer Statement</h3>
          <p className="text-sm text-gray-600">{reportData.period?.from} - {reportData.period?.to}</p>
        </div>

        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">Customer</p>
              <p className="text-base font-semibold text-gray-900 mt-1">{customer.name || 'Customer'}</p>
              {customer.phone ? <p className="text-sm text-gray-600 mt-1">{customer.phone}</p> : null}
              {customer.email ? <p className="text-sm text-gray-600">{customer.email}</p> : null}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">Credit Terms</p>
              <div className="mt-1">
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${getCreditTermsBadgeClasses(normalizeCreditTerms(customer.credit_terms))}`}
                >
                  {normalizeCreditTerms(customer.credit_terms)}
                </span>
              </div>
              {hasPositiveCreditLimit(customer.credit_limit) ? (
                <p className="text-sm text-gray-600 mt-2">Credit Limit {formatCurrency(customer.credit_limit)}</p>
              ) : null}
            </div>
            {reportData.age_analysis ? (
              <div>
                <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">Age Analysis</p>
                <p className="text-sm text-gray-600 mt-1">Current {formatCurrency(reportData.age_analysis.current)}</p>
                <p className="text-sm text-gray-600">30+ {formatCurrency(reportData.age_analysis.days_1_30)} · 60+ {formatCurrency(reportData.age_analysis.days_31_60)}</p>
                <p className="text-sm text-gray-600">90+ {formatCurrency(reportData.age_analysis.days_61_90 + reportData.age_analysis.days_over_90)}</p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Opening Balance</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.opening_balance ?? reportData.opening_balance)}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Total Invoiced</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_invoiced ?? reportData.total_debits)}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Total Payments</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_payments)}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Total Credit Notes</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_credit_notes)}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Total Credits</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_credits ?? reportData.total_credits)}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Closing Balance</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.closing_balance ?? reportData.closing_balance)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doc Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Running Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.map((transaction: any, index: number) => (
                  <tr key={`${transaction.doc_number || transaction.reference || 'tx'}-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{transaction.date ? new Date(transaction.date).toLocaleDateString('en-ZA') : '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{transaction.type || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{transaction.doc_number || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{transaction.reference || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{transaction.description || '—'}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(transaction.debit)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(transaction.credit)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(transaction.balance)}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">{formatCurrency(transaction.running_balance ?? transaction.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderCashFlow = () => {
    if (!reportData) return null;

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Cash Flow Statement</h3>
          <p className="text-sm text-gray-600">
            {reportData.period?.from} - {reportData.period?.to}
          </p>
        </div>

        {/* Operating Activities */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4 border-b pb-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-gray-900 text-lg">Cash Flows from Operating Activities</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Net Profit</span>
              <span>{formatCurrency(reportData.operating?.net_profit)}</span>
            </div>
            <div className="pl-4 space-y-1 text-sm text-gray-500">
              <p className="font-medium text-gray-700">Adjustments:</p>
              <div className="flex justify-between">
                <span>Depreciation</span>
                <span>{formatCurrency(reportData.operating?.depreciation)}</span>
              </div>
              <div className="flex justify-between">
                <span>Accounts Receivable Change</span>
                <span className={reportData.operating?.accounts_receivable_change < 0 ? 'text-red-600' : ''}>
                  {formatCurrency(reportData.operating?.accounts_receivable_change)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Inventory Change</span>
                <span>{formatCurrency(reportData.operating?.inventory_change)}</span>
              </div>
              <div className="flex justify-between">
                <span>Accounts Payable Change</span>
                <span className={reportData.operating?.accounts_payable_change > 0 ? 'text-green-600' : ''}>
                  {formatCurrency(reportData.operating?.accounts_payable_change)}
                </span>
              </div>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
              <span>Net Cash from Operations</span>
              <span className={reportData.operating?.net_cash_from_operations >= 0 ? 'text-green-600' : 'text-red-600'}>
                {formatCurrency(reportData.operating?.net_cash_from_operations)}
              </span>
            </div>
          </div>
        </div>

        {/* Investing Activities */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4 border-b pb-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-gray-900 text-lg">Cash Flows from Investing Activities</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Asset Purchases</span>
              <span className="text-red-600">{formatCurrency(reportData.investing?.asset_purchases)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Asset Sales</span>
              <span>{formatCurrency(reportData.investing?.asset_sales)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Investments</span>
              <span>{formatCurrency(reportData.investing?.investments)}</span>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
              <span>Net Cash from Investing</span>
              <span className={reportData.investing?.net_cash_from_investing >= 0 ? 'text-green-600' : 'text-red-600'}>
                {formatCurrency(reportData.investing?.net_cash_from_investing)}
              </span>
            </div>
          </div>
        </div>

        {/* Financing Activities */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4 border-b pb-2">
            <TrendingDown className="w-5 h-5 text-orange-600" />
            <h4 className="font-semibold text-gray-900 text-lg">Cash Flows from Financing Activities</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Debt Proceeds</span>
              <span>{formatCurrency(reportData.financing?.debt_proceeds)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Debt Repayments</span>
              <span className="text-red-600">{formatCurrency(reportData.financing?.debt_repayments)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Equity Proceeds</span>
              <span>{formatCurrency(reportData.financing?.equity_proceeds)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Dividends Paid</span>
              <span className="text-red-600">{formatCurrency(reportData.financing?.dividends_paid)}</span>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
              <span>Net Cash from Financing</span>
              <span className={reportData.financing?.net_cash_from_financing >= 0 ? 'text-green-600' : 'text-red-600'}>
                {formatCurrency(reportData.financing?.net_cash_from_financing)}
              </span>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
          <div className="space-y-3">
            <div className="flex justify-between text-lg font-semibold">
              <span>Net Change in Cash</span>
              <span className={reportData.net_change_in_cash >= 0 ? 'text-green-600' : 'text-red-600'}>
                {formatCurrency(reportData.net_change_in_cash)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Opening Cash Balance</span>
              <span>{formatCurrency(reportData.opening_cash_balance)}</span>
            </div>
            <div className="flex justify-between text-xl font-bold border-t pt-2 mt-2">
              <span>Closing Cash Balance</span>
              <span className="text-blue-600">{formatCurrency(reportData.closing_cash_balance)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // VAT Report Renderer
  const renderVatReport = () => {
    if (!reportData) return null;

    const outputTax = reportData.output_tax || 0;
    const inputTax = reportData.input_tax || 0;
    const netLiability = reportData.net_tax_liability || (outputTax - inputTax);

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">VAT Report</h3>
          {reportData.period && (
            <p className="text-sm text-gray-600">
              {reportData.period.from} - {reportData.period.to}
            </p>
          )}
        </div>

        {/* VAT Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Output VAT */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Output VAT (Sales)</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(outputTax)}</p>
            <p className="text-xs text-gray-500 mt-1">VAT collected from customers</p>
          </div>

          {/* Input VAT */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingDown className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Input VAT (Purchases)</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(inputTax)}</p>
            <p className="text-xs text-gray-500 mt-1">VAT paid to suppliers</p>
          </div>

          {/* Net VAT Liability */}
          <div className={`rounded-lg border p-6 ${netLiability >= 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${netLiability >= 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                <DollarSign className={`w-5 h-5 ${netLiability >= 0 ? 'text-red-600' : 'text-green-600'}`} />
              </div>
              <span className="text-sm font-medium text-gray-600">
                {netLiability >= 0 ? 'Net VAT payable' : 'Net VAT refundable'}
              </span>
            </div>
            <p className={`text-2xl font-bold ${netLiability >= 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(Math.abs(netLiability))}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {netLiability >= 0 ? 'Amount to pay' : 'Amount to claim'}
            </p>
          </div>
        </div>

        {/* VAT Calculation Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900">VAT Calculation</h4>
          </div>
          <div className="p-6 space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Output VAT on Sales</span>
              <span className="font-medium">{formatCurrency(outputTax)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Less: Input VAT on Purchases</span>
              <span className="font-medium text-red-600">({formatCurrency(inputTax)})</span>
            </div>
            <div className="flex justify-between py-3 font-bold text-lg">
              <span>{netLiability >= 0 ? 'Net VAT Payable' : 'Net VAT Refundable'}</span>
              <span className={netLiability >= 0 ? 'text-red-600' : 'text-green-600'}>
                {formatCurrency(Math.abs(netLiability))}
              </span>
            </div>
          </div>
        </div>

        {/* Tax Rates Reference */}
        {reportData.tax_rates && reportData.tax_rates.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900">Tax Rates Applied</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reportData.tax_rates.map((rate: any) => (
                    <tr key={rate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{rate.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{rate.code}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">{rate.rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderInventoryEmptyState = (title: string, message: string) => (
    <div className="bg-white rounded-lg border border-dashed border-gray-300 p-10 text-center text-gray-500">
      <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );

  const renderInsightCard = ({
    title,
    value,
    subtitle,
    tone = 'blue'
  }: {
    title: string;
    value: string;
    subtitle: string;
    tone?: 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'slate';
  }) => {
    const tones = {
      blue: 'bg-blue-50 border-blue-200 text-blue-700',
      green: 'bg-green-50 border-green-200 text-green-700',
      amber: 'bg-amber-50 border-amber-200 text-amber-700',
      red: 'bg-red-50 border-red-200 text-red-700',
      purple: 'bg-purple-50 border-purple-200 text-purple-700',
      slate: 'bg-slate-50 border-slate-200 text-slate-700',
    } as const;

    return (
      <div className={`rounded-lg border p-4 ${tones[tone]}`}>
        <p className="text-xs uppercase tracking-wide font-semibold opacity-80">{title}</p>
        <p className="text-lg font-bold mt-1">{value}</p>
        <p className="text-xs mt-2 opacity-80">{subtitle}</p>
      </div>
    );
  };

  const renderInventoryValuation = () => {
    if (!reportData) return null;

    const items = reportData.items || [];
    const summary = reportData.summary || {};
    const highestValueItem = items.reduce((top: any, item: any) =>
      toNumber(item.stock_value) > toNumber(top?.stock_value) ? item : top,
      items[0]);
    const highestQuantityItem = items.reduce((top: any, item: any) =>
      toNumber(item.quantity) > toNumber(top?.quantity) ? item : top,
      items[0]);
    const strongestMarkupItem = items.reduce((top: any, item: any) => {
      const markup = toNumber(item.sale_price) - toNumber(item.cost_price);
      const topMarkup = toNumber(top?.sale_price) - toNumber(top?.cost_price);
      return markup > topMarkup ? item : top;
    }, items[0]);
    const zeroStockCount = items.filter((item: any) => toNumber(item.quantity) <= 0).length;
    const reconciliationWarnings = reportData.reconciliation_warnings || [];
    const totalRetailValue = items.reduce((sum: number, item: any) => sum + toNumber(item.sale_price) * toNumber(item.quantity), 0);

    if (items.length === 0) {
      return renderInventoryEmptyState(
        'No inventory valuation data',
        'No stock valuation records matched the selected as-of date and warehouse filter.'
      );
    }

    const valuationMethodLabels: Record<string, string> = {
      fifo: 'FIFO',
      lifo: 'LIFO',
      weighted_average: 'Moving Average',
    };

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Inventory Valuation</h3>
          <p className="text-sm text-gray-600">As of {reportData.as_of_date}</p>
          <div className="mt-2 flex items-center justify-center gap-2">
            {items.some((i: any) => i.valuation_method) && (() => {
              const methods = items.map((i: any) => i.valuation_method).filter(Boolean) as string[];
              const uniqueMethods = [...new Set(methods)];
              return uniqueMethods.map((m) => (
                <span key={m} className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                  {valuationMethodLabels[m] || m}
                </span>
              ));
            })()}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-blue-600">Products</p>
            <p className="text-2xl font-bold text-blue-700">{summary.total_products ?? items.length}</p>
          </div>
          <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
            <p className="text-sm text-cyan-600">Total Quantity</p>
            <p className="text-2xl font-bold text-cyan-700">{formatQuantity(summary.total_quantity)}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600">Inventory Value</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.total_value)}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <p className="text-sm text-purple-600">Average Unit Cost</p>
            <p className="text-2xl font-bold text-purple-700">{formatCurrency(summary.average_cost)}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <p className="text-sm text-emerald-600">Retail Value</p>
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalRetailValue)}</p>
          </div>
        </div>

        {reconciliationWarnings.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                </svg>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-amber-800">
                  {reconciliationWarnings.length} item{reconciliationWarnings.length !== 1 ? 's' : ''} with quantity discrepancies
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Movement-calculated quantity differs from quantity_on_hand. Review stock adjustments or missing movements.
                </p>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-amber-600">
                        <th className="pr-4 py-1 font-medium">Product</th>
                        <th className="pr-4 py-1 font-medium text-right">Expected</th>
                        <th className="pr-4 py-1 font-medium text-right">Actual</th>
                        <th className="pr-4 py-1 font-medium text-right">Discrepancy</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {reconciliationWarnings.slice(0, 10).map((w: any) => (
                        <tr key={w.item_id}>
                          <td className="pr-4 py-1 text-amber-900">{w.product_name} <span className="font-mono text-amber-500">{w.sku}</span></td>
                          <td className="pr-4 py-1 text-right text-amber-700">{formatQuantity(w.expected_qty)}</td>
                          <td className="pr-4 py-1 text-right text-amber-700">{formatQuantity(w.actual_qty)}</td>
                          <td className={`pr-4 py-1 text-right font-medium ${w.discrepancy > 0 ? 'text-amber-800' : 'text-red-700'}`}>
                            {w.discrepancy > 0 ? '+' : ''}{formatQuantity(w.discrepancy)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reconciliationWarnings.length > 10 && (
                    <p className="text-xs text-amber-600 mt-2">...and {reconciliationWarnings.length - 10} more</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {renderInsightCard({
            title: 'Highest stock value',
            value: highestValueItem?.product_name || '—',
            subtitle: `${formatCurrency(highestValueItem?.stock_value)} · ${highestValueItem?.warehouse_name || 'No warehouse'}`,
            tone: 'green',
          })}
          {renderInsightCard({
            title: 'Largest quantity holding',
            value: highestQuantityItem?.product_name || '—',
            subtitle: `${formatQuantity(highestQuantityItem?.quantity)} units on hand`,
            tone: 'blue',
          })}
          {renderInsightCard({
            title: 'Best unit markup',
            value: strongestMarkupItem?.product_name || '—',
            subtitle: `${formatCurrency(toNumber(strongestMarkupItem?.sale_price) - toNumber(strongestMarkupItem?.cost_price))} per unit`,
            tone: 'purple',
          })}
          {renderInsightCard({
            title: 'Zero-stock products',
            value: zeroStockCount.toString(),
            subtitle: 'Products currently sitting at zero quantity',
            tone: zeroStockCount > 0 ? 'amber' : 'slate',
          })}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Warehouse</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg Cost</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost Price</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sale Price</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock Value</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Retail Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item: any) => (
                  <tr key={`${item.id}-${item.warehouse_code || 'default'}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{item.product_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{item.sku || '—'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {item.warehouse_name || 'Unassigned'}
                      {item.warehouse_code ? <div className="text-xs text-gray-500 font-mono">{item.warehouse_code}</div> : null}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                        {valuationMethodLabels[item.valuation_method] || item.valuation_method || 'FIFO'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatQuantity(item.quantity)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(item.average_cost)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(item.cost_price)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(item.sale_price)}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-green-700">{formatCurrency(item.stock_value)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-700">{formatCurrency(toNumber(item.sale_price) * toNumber(item.quantity))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderStockMovementTypeBadge = (movementType: string) => {
    const inboundTypes = new Set(['purchase', 'adjustment_in', 'transfer_in', 'return']);
    const outboundTypes = new Set(['sale', 'adjustment_out', 'transfer_out', 'consumption', 'write_off']);

    if (inboundTypes.has(movementType)) {
      return <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">{movementType.replace(/_/g, ' ')}</span>;
    }

    if (outboundTypes.has(movementType)) {
      return <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">{movementType.replace(/_/g, ' ')}</span>;
    }

    return <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{movementType.replace(/_/g, ' ')}</span>;
  };

  const renderStockMovement = () => {
    if (!reportData) return null;

    const movements = reportData.movements || [];
    const summary = reportData.summary || {};
    const productMovementMap = new Map<string, { productName: string; quantity: number; movementCount: number }>();
    const warehouseMovementMap = new Map<string, { warehouseName: string; movementCount: number; totalQuantity: number }>();

    movements.forEach((movement: any) => {
      const quantity = Math.abs(toNumber(movement.quantity));
      const productKey = String(movement.work_item_id || movement.sku || movement.product_name || movement.id);
      const warehouseKey = String(movement.warehouse_id || movement.warehouse_name || 'unknown');

      const productEntry = productMovementMap.get(productKey) || {
        productName: movement.product_name || 'Unnamed product',
        quantity: 0,
        movementCount: 0,
      };
      productEntry.quantity += quantity;
      productEntry.movementCount += 1;
      productMovementMap.set(productKey, productEntry);

      const warehouseEntry = warehouseMovementMap.get(warehouseKey) || {
        warehouseName: movement.warehouse_name || 'Unknown warehouse',
        movementCount: 0,
        totalQuantity: 0,
      };
      warehouseEntry.movementCount += 1;
      warehouseEntry.totalQuantity += quantity;
      warehouseMovementMap.set(warehouseKey, warehouseEntry);
    });

    const topMover = Array.from(productMovementMap.values()).sort((a, b) => b.quantity - a.quantity)[0];
    const busiestWarehouse = Array.from(warehouseMovementMap.values()).sort((a, b) => b.movementCount - a.movementCount)[0];
    const largestInbound = movements
      .filter((movement: any) => ['purchase', 'adjustment_in', 'transfer_in', 'return'].includes(movement.movement_type))
      .sort((a: any, b: any) => Math.abs(toNumber(b.quantity)) - Math.abs(toNumber(a.quantity)))[0];
    const largestOutbound = movements
      .filter((movement: any) => !['purchase', 'adjustment_in', 'transfer_in', 'return'].includes(movement.movement_type))
      .sort((a: any, b: any) => Math.abs(toNumber(b.quantity)) - Math.abs(toNumber(a.quantity)))[0];

    if (movements.length === 0) {
      return renderInventoryEmptyState(
        'No stock movement data',
        'No inventory movements matched the selected period and filters.'
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Stock Movement Report</h3>
          <p className="text-sm text-gray-600">{reportData.period?.from} - {reportData.period?.to}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600">Total In</p>
            <p className="text-2xl font-bold text-green-700">{formatQuantity(summary.total_in)}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-orange-600">Total Out</p>
            <p className="text-2xl font-bold text-orange-700">{formatQuantity(summary.total_out)}</p>
          </div>
          <div className={`rounded-lg p-4 border ${Number(summary.net_movement) >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
            <p className={`text-sm ${Number(summary.net_movement) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>Net Movement</p>
            <p className={`text-2xl font-bold ${Number(summary.net_movement) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatQuantity(summary.net_movement)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600">Transactions</p>
            <p className="text-2xl font-bold text-gray-900">{summary.transaction_count ?? movements.length}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {renderInsightCard({
            title: 'Top moving product',
            value: topMover?.productName || '—',
            subtitle: `${formatQuantity(topMover?.quantity)} units across ${topMover?.movementCount ?? 0} movements`,
            tone: 'blue',
          })}
          {renderInsightCard({
            title: 'Busiest warehouse',
            value: busiestWarehouse?.warehouseName || '—',
            subtitle: `${busiestWarehouse?.movementCount ?? 0} transactions · ${formatQuantity(busiestWarehouse?.totalQuantity)} units handled`,
            tone: 'purple',
          })}
          {renderInsightCard({
            title: 'Largest inbound',
            value: largestInbound?.product_name || '—',
            subtitle: `${formatQuantity(Math.abs(toNumber(largestInbound?.quantity)))} units via ${(largestInbound?.movement_type || 'movement').replace(/_/g, ' ')}`,
            tone: 'green',
          })}
          {renderInsightCard({
            title: 'Largest outbound',
            value: largestOutbound?.product_name || '—',
            subtitle: `${formatQuantity(Math.abs(toNumber(largestOutbound?.quantity)))} units via ${(largestOutbound?.movement_type || 'movement').replace(/_/g, ' ')}`,
            tone: 'amber',
          })}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Warehouse</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Movement Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {movements.map((movement: any) => {
                  const numericQuantity = Number(movement.quantity) || 0;
                  const isInbound = ['purchase', 'adjustment_in', 'transfer_in', 'return'].includes(movement.movement_type);

                  return (
                    <tr key={movement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {movement.created_at ? new Date(movement.created_at).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{movement.product_name || 'Unnamed product'}</div>
                        <div className="text-xs text-gray-500 font-mono">{movement.sku || '—'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{movement.warehouse_name || '—'}</td>
                      <td className="px-6 py-4 text-sm">{renderStockMovementTypeBadge(movement.movement_type || 'unknown')}</td>
                      <td className={`px-6 py-4 text-sm text-right font-semibold ${isInbound ? 'text-green-700' : 'text-orange-700'}`}>
                        {isInbound ? '+' : '-'}{formatQuantity(Math.abs(numericQuantity))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderLowStockStatusBadge = (item: any) => {
    const onHand = Number(item.quantity_on_hand) || 0;
    const available = Number(item.quantity_available) || 0;

    if (onHand <= 0 || available <= 0) {
      return <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Out of stock</span>;
    }

    return <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Below reorder level</span>;
  };

  const renderLowStockReport = () => {
    if (!reportData) return null;

    const items = reportData.items || [];
    const summary = reportData.summary || {};
    const mostUrgentItem = items.reduce((top: any, item: any) => {
      const shortage = Math.max(toNumber(item.reorder_level) - toNumber(item.quantity_available), 0);
      const topShortage = Math.max(toNumber(top?.reorder_level) - toNumber(top?.quantity_available), 0);
      return shortage > topShortage ? item : top;
    }, items[0]);
    const highestReorderSpendItem = items.reduce((top: any, item: any) => {
      const reorderValue = toNumber(item.reorder_quantity) * toNumber(item.cost_price);
      const topReorderValue = toNumber(top?.reorder_quantity) * toNumber(top?.cost_price);
      return reorderValue > topReorderValue ? item : top;
    }, items[0]);
    const warehouseRiskMap = new Map<string, { warehouseName: string; count: number }>();
    items.forEach((item: any) => {
      const key = String(item.warehouse_code || item.warehouse_name || 'unassigned');
      const current = warehouseRiskMap.get(key) || { warehouseName: item.warehouse_name || 'Unassigned', count: 0 };
      current.count += 1;
      warehouseRiskMap.set(key, current);
    });
    const mostAffectedWarehouse = Array.from(warehouseRiskMap.values()).sort((a, b) => b.count - a.count)[0];
    const stockoutRate = items.length > 0 ? (toNumber(summary.out_of_stock_count) / items.length) * 100 : 0;

    if (items.length === 0) {
      return renderInventoryEmptyState(
        'No low stock items',
        'All tracked products are above their reorder levels for the selected warehouse scope.'
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Low Stock Alert Report</h3>
          <p className="text-sm text-gray-600">Products that need replenishment based on current reorder thresholds.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <p className="text-sm text-yellow-600">Low Stock Items</p>
            <p className="text-2xl font-bold text-yellow-700">{summary.low_stock_count ?? 0}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-sm text-red-600">Out of Stock</p>
            <p className="text-2xl font-bold text-red-700">{summary.out_of_stock_count ?? 0}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-orange-600">Total Flagged</p>
            <p className="text-2xl font-bold text-orange-700">{summary.total_items ?? items.length}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-blue-600">Estimated Reorder Value</p>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(summary.total_reorder_value)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {renderInsightCard({
            title: 'Most urgent shortage',
            value: mostUrgentItem?.product_name || '—',
            subtitle: `${formatQuantity(Math.max(toNumber(mostUrgentItem?.reorder_level) - toNumber(mostUrgentItem?.quantity_available), 0))} units below target`,
            tone: 'red',
          })}
          {renderInsightCard({
            title: 'Highest reorder spend',
            value: highestReorderSpendItem?.product_name || '—',
            subtitle: `${formatCurrency(toNumber(highestReorderSpendItem?.reorder_quantity) * toNumber(highestReorderSpendItem?.cost_price))} estimated cost`,
            tone: 'blue',
          })}
          {renderInsightCard({
            title: 'Most affected warehouse',
            value: mostAffectedWarehouse?.warehouseName || '—',
            subtitle: `${mostAffectedWarehouse?.count ?? 0} flagged products`,
            tone: 'amber',
          })}
          {renderInsightCard({
            title: 'Stockout rate',
            value: formatPercent(stockoutRate),
            subtitle: 'Share of flagged products already fully out of stock',
            tone: stockoutRate > 50 ? 'red' : 'slate',
          })}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Warehouse</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">On Hand</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Available</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reorder Level</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reorder Qty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item: any) => (
                  <tr key={`${item.id}-${item.warehouse_code || 'default'}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{item.product_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{item.sku || '—'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.warehouse_name || 'Unassigned'}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatQuantity(item.quantity_on_hand)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatQuantity(item.quantity_available)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatQuantity(item.reorder_level)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatQuantity(item.reorder_quantity)}</td>
                    <td className="px-6 py-4 text-sm">{renderLowStockStatusBadge(item)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderWarehouseSummary = () => {
    if (!reportData) return null;

    const warehouses = reportData.warehouses || [];
    const summary = reportData.summary || {};
    const totals = reportData.totals || {};
    const strongestWarehouse = warehouses.reduce((top: any, warehouse: any) =>
      toNumber(warehouse.total_value) > toNumber(top?.total_value) ? warehouse : top,
      warehouses[0]);
    const broadestWarehouse = warehouses.reduce((top: any, warehouse: any) =>
      toNumber(warehouse.product_count) > toNumber(top?.product_count) ? warehouse : top,
      warehouses[0]);
    const riskiestWarehouse = warehouses.reduce((top: any, warehouse: any) => {
      const riskCount = toNumber(warehouse.low_stock_count) + toNumber(warehouse.out_of_stock_count);
      const topRiskCount = toNumber(top?.low_stock_count) + toNumber(top?.out_of_stock_count);
      return riskCount > topRiskCount ? warehouse : top;
    }, warehouses[0]);
    const mostDenseWarehouse = warehouses.reduce((top: any, warehouse: any) => {
      const density = toNumber(warehouse.product_count) > 0
        ? toNumber(warehouse.total_value) / toNumber(warehouse.product_count)
        : 0;
      const topDensity = toNumber(top?.product_count) > 0
        ? toNumber(top?.total_value) / toNumber(top?.product_count)
        : 0;
      return density > topDensity ? warehouse : top;
    }, warehouses[0]);

    if (warehouses.length === 0) {
      return renderInventoryEmptyState(
        'No warehouse summary data',
        'No active warehouses are configured for this client yet.'
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Warehouse Summary</h3>
          <p className="text-sm text-gray-600">As of {reportData.as_of_date}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
            <p className="text-sm text-cyan-600">Warehouses</p>
            <p className="text-2xl font-bold text-cyan-700">{summary.warehouse_count ?? warehouses.length}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-blue-600">Tracked Products</p>
            <p className="text-2xl font-bold text-blue-700">{totals.product_count ?? 0}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <p className="text-sm text-orange-600">Low / Out of Stock</p>
            <p className="text-2xl font-bold text-orange-700">{(totals.low_stock_count ?? 0) + (totals.out_of_stock_count ?? 0)}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600">Total Inventory Value</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.total_inventory_value ?? totals.total_value)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {renderInsightCard({
            title: 'Highest value warehouse',
            value: strongestWarehouse?.name || '—',
            subtitle: `${formatCurrency(strongestWarehouse?.total_value)} on hand`,
            tone: 'green',
          })}
          {renderInsightCard({
            title: 'Broadest range',
            value: broadestWarehouse?.name || '—',
            subtitle: `${broadestWarehouse?.product_count ?? 0} stocked products`,
            tone: 'blue',
          })}
          {renderInsightCard({
            title: 'Most at risk',
            value: riskiestWarehouse?.name || '—',
            subtitle: `${toNumber(riskiestWarehouse?.low_stock_count) + toNumber(riskiestWarehouse?.out_of_stock_count)} low/out-of-stock lines`,
            tone: 'amber',
          })}
          {renderInsightCard({
            title: 'Best value density',
            value: mostDenseWarehouse?.name || '—',
            subtitle: `${formatCurrency(toNumber(mostDenseWarehouse?.product_count) > 0 ? toNumber(mostDenseWarehouse?.total_value) / toNumber(mostDenseWarehouse?.product_count) : 0)} per stocked product`,
            tone: 'purple',
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {warehouses.map((warehouse: any) => (
            <div key={warehouse.id} className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{warehouse.name}</h4>
                  <div className="text-sm text-gray-500 font-mono">{warehouse.code || '—'} · {warehouse.warehouse_type || 'warehouse'}</div>
                </div>
                <Warehouse className="w-6 h-6 text-cyan-600 flex-shrink-0" />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Products</p>
                  <p className="text-xl font-bold text-gray-900">{warehouse.product_count ?? 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Quantity</p>
                  <p className="text-xl font-bold text-gray-900">{formatQuantity(warehouse.total_quantity)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Low Stock</p>
                  <p className="text-xl font-bold text-orange-700">{warehouse.low_stock_count ?? 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Out of Stock</p>
                  <p className="text-xl font-bold text-red-700">{warehouse.out_of_stock_count ?? 0}</p>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm border-t border-gray-200 pt-3">
                <span className="text-gray-600">Inventory value</span>
                <span className="font-semibold text-green-700">{formatCurrency(warehouse.total_value)}</span>
              </div>
              {(warehouse.address_line1 || warehouse.city) && (
                <p className="text-xs text-gray-500 mt-2">{[warehouse.address_line1, warehouse.city].filter(Boolean).join(', ')}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderProductProfitability = () => {
    if (!reportData) return null;

    const products = reportData.products || [];
    const summary = reportData.summary || {};
    const topProfitProduct = products.reduce((top: any, product: any) =>
      toNumber(product.profit) > toNumber(top?.profit) ? product : top,
      products[0]);
    const topRevenueProduct = products.reduce((top: any, product: any) =>
      toNumber(product.revenue) > toNumber(top?.revenue) ? product : top,
      products[0]);
    const bestMarginProduct = products.reduce((top: any, product: any) =>
      toNumber(product.margin_percentage) > toNumber(top?.margin_percentage) ? product : top,
      products[0]);
    const lossMakingCount = products.filter((product: any) => toNumber(product.profit) < 0).length;

    if (products.length === 0) {
      return renderInventoryEmptyState(
        'No product profitability data',
        'No invoiced product sales matched the selected period and category filter.'
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Product Profitability</h3>
          <p className="text-sm text-gray-600">{reportData.period?.from} - {reportData.period?.to}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-blue-600">Products</p>
            <p className="text-2xl font-bold text-blue-700">{summary.total_products ?? products.length}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-green-600">Revenue</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(summary.total_revenue)}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <p className="text-sm text-red-600">Cost</p>
            <p className="text-2xl font-bold text-red-700">{formatCurrency(summary.total_cost)}</p>
          </div>
          <div className={`rounded-lg p-4 border ${Number(summary.total_profit) >= 0 ? 'bg-purple-50 border-purple-200' : 'bg-red-50 border-red-200'}`}>
            <p className={`text-sm ${Number(summary.total_profit) >= 0 ? 'text-purple-600' : 'text-red-600'}`}>Profit / Margin</p>
            <p className={`text-2xl font-bold ${Number(summary.total_profit) >= 0 ? 'text-purple-700' : 'text-red-700'}`}>{formatCurrency(summary.total_profit)}</p>
            <p className="text-xs text-gray-500 mt-1">{summary.overall_margin ?? 0}% overall margin</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {renderInsightCard({
            title: 'Top profit contributor',
            value: topProfitProduct?.item_name || '—',
            subtitle: `${formatCurrency(topProfitProduct?.profit)} gross profit`,
            tone: 'green',
          })}
          {renderInsightCard({
            title: 'Top revenue product',
            value: topRevenueProduct?.item_name || '—',
            subtitle: `${formatCurrency(topRevenueProduct?.revenue)} revenue generated`,
            tone: 'blue',
          })}
          {renderInsightCard({
            title: 'Best margin',
            value: bestMarginProduct?.item_name || '—',
            subtitle: `${formatPercent(bestMarginProduct?.margin_percentage)} margin`,
            tone: 'purple',
          })}
          {renderInsightCard({
            title: 'Loss-making products',
            value: lossMakingCount.toString(),
            subtitle: 'Products generating negative gross profit in the selected period',
            tone: lossMakingCount > 0 ? 'red' : 'slate',
          })}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty Sold</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {products.map((product: any) => {
                  const margin = Number(product.margin_percentage) || 0;
                  return (
                    <tr key={product.item_id || `${product.item_name}-${product.sku}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{product.item_name || 'Unnamed product'}</div>
                        <div className="text-xs text-gray-500 font-mono">{product.sku || '—'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{formatQuantity(product.quantity_sold)}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(product.revenue)}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(product.cost)}</td>
                      <td className={`px-6 py-4 text-sm text-right font-semibold ${Number(product.profit) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(product.profit)}
                      </td>
                      <td className={`px-6 py-4 text-sm text-right font-semibold ${margin >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                        {margin.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderGenericTable = () => {
    if (!reportData) return null;

    // Find the main data array in the report
    let rows: any[] = [];
    const title = report.name;

    if (reportData.accounts) rows = reportData.accounts;
    else if (reportData.customers) rows = reportData.customers;
    else if (reportData.suppliers) rows = reportData.suppliers;
    else if (reportData.products) rows = reportData.products;
    else if (reportData.entries) rows = reportData.entries;
    else if (reportData.transactions) rows = reportData.transactions;
    else if (reportData.invoices) rows = reportData.invoices;
    else if (reportData.bills) rows = reportData.bills;
    else if (reportData.tax_rates) rows = reportData.tax_rates;
    else if (reportData.statement) rows = reportData.statement;
    else if (reportData.items) rows = reportData.items;
    else if (reportData.movements) rows = reportData.movements;
    else if (reportData.warehouses) rows = reportData.warehouses;

    if (rows.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No data available for this report
        </div>
      );
    }

    const columns = Object.keys(rows[0]).filter(k => !k.startsWith('_'));

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          {reportData.period && (
            <p className="text-sm text-gray-600">
              {reportData.period.from} - {reportData.period.to}
            </p>
          )}
        </div>

        {/* Summary if exists */}
        {reportData.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(reportData.summary).map(([key, value]) => (
              typeof value !== 'object' && (
                <div key={key} className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">{key.replace(/_/g, ' ')}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {typeof value === 'number'
                      ? genericFieldKeyLooksLikeMoney(key)
                        ? formatCurrency(value)
                        : value.toLocaleString(undefined, {
                          minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
                          maximumFractionDigits: 2,
                        })
                      : String(value)}
                  </p>
                </div>
              )
            ))}
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {columns.map(col => (
                    <th key={col} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {col.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {columns.map(col => (
                      <td key={col} className="px-6 py-4 text-sm text-gray-900">
                        {typeof row[col] === 'number'
                          ? genericFieldKeyLooksLikeMoney(col)
                            ? formatCurrency(row[col])
                            : row[col].toLocaleString(undefined, {
                              minimumFractionDigits: Number.isInteger(row[col]) ? 0 : 2,
                              maximumFractionDigits: 2,
                            })
                          : row[col]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderBankReconciliation = () => {
    if (!reportData) return null;
    const { summary, by_account: byAccount, transactions, rules_snapshot: rules } = reportData;
    const subtitle = typeof reportData.meta?.subtitle === 'string' ? reportData.meta.subtitle : '';

    return (
      <div className="space-y-6">
        <div className="text-center mb-2">
          <h3 className="text-xl font-bold text-gray-900 flex items-center justify-center gap-2">
            <Wallet className="w-6 h-6 text-emerald-600" />
            Bank reconciliation activity
          </h3>
          {reportData.period && (
            <p className="text-sm text-gray-600">
              {reportData.period.from} — {reportData.period.to}
            </p>
          )}
          {subtitle ? (
            <p className="text-xs text-gray-500 mt-2 max-w-3xl mx-auto">{subtitle}</p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {renderInsightCard({
            title: 'In period',
            value: String(summary?.transactions_in_period ?? 0),
            subtitle: 'Bank transactions',
            tone: 'slate',
          })}
          {renderInsightCard({
            title: 'Unmatched',
            value: String(summary?.unmatched_count ?? 0),
            subtitle: 'Needs review or rules',
            tone: 'red',
          })}
          {renderInsightCard({
            title: 'Matched',
            value: String(summary?.matched_count ?? 0),
            subtitle: 'Applied to documents / payments',
            tone: 'green',
          })}
          {renderInsightCard({
            title: 'Excluded',
            value: String(summary?.excluded_count ?? 0),
            subtitle: 'Non-allocatable rows',
            tone: 'amber',
          })}
          {renderInsightCard({
            title: 'Open suggestions',
            value: String(summary?.open_suggestions ?? 0),
            subtitle: 'Suggestion review queue',
            tone: 'blue',
          })}
          {renderInsightCard({
            title: 'Active rules',
            value: String(summary?.active_rules ?? 0),
            subtitle: 'ReconciliationRulesManager',
            tone: 'purple',
          })}
        </div>

        {Array.isArray(byAccount) && byAccount.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900">Summary by bank account</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bank</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">CCY</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Txns</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unmatched</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Matched</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Debit Σ</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credit Σ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {byAccount.map((row: any) => (
                    <tr key={`${row.bank_account_name}-${row.bank_name}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-900">{row.bank_account_name}</td>
                      <td className="px-4 py-2 text-gray-600">{row.bank_name}</td>
                      <td className="px-4 py-2 text-center text-gray-700">{row.currency}</td>
                      <td className="px-4 py-2 text-right">{row.txn_count}</td>
                      <td className="px-4 py-2 text-right text-red-600 font-medium">{row.unmatched_count}</td>
                      <td className="px-4 py-2 text-right text-green-700">{row.matched_count}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(row.debit_volume)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(row.credit_volume)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {Array.isArray(transactions) && transactions.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900">
                Transactions
                {summary?.truncated ? (
                  <span className="text-sm font-normal text-amber-700 ml-2">
                    (newest {transactions.length} shown)
                  </span>
                ) : null}
              </h4>
            </div>
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Matched to</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transactions.map((row: any) => (
                    <tr key={row.id || `${row.transaction_date}-${row.bank_account_name}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">{row.transaction_date}</td>
                      <td className="px-3 py-2 text-gray-600">{row.bank_account_name}</td>
                      <td className="px-3 py-2 text-gray-900 max-w-xs truncate" title={row.description}>
                        {row.description}
                      </td>
                      <td className="px-3 py-2 text-center uppercase text-xs">{row.transaction_type}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(row.amount)}</td>
                      <td className="px-3 py-2 text-center text-xs">{row.reconciliation_status}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{row.matched_ref}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          renderInventoryEmptyState('No transactions', 'No bank transactions in this period for the selected filters.')
        )}

        {Array.isArray(rules) && rules.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900">Reconciliation rules snapshot</h4>
              <p className="text-xs text-gray-500 mt-1">
                Same source as ReconciliationRulesManager — match counts are historical on the rule record.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Matches</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last matched</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rules.map((row: any) => (
                    <tr key={row.rule_name} className="hover:bg-gray-50">
                      <td className="px-4 py-2">{row.rule_name}</td>
                      <td className="px-4 py-2 text-center">{row.is_active ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-2 text-right">{row.match_count}</td>
                      <td className="px-4 py-2 text-gray-600">{row.last_matched_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderInventoryOrderPipeline = () => {
    if (!reportData) return null;
    const documents = reportData.documents || [];
    const summary = reportData.summary || {};
    const types = (reportData.order_document_types || []).join(', ') || 'sales_order, purchase_order, work_order';

    if (!documents.length) {
      return renderInventoryEmptyState(
        'No documents in range',
        'Try widening the date range or including all document types (sales orders, purchase orders, work orders).',
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center mb-2">
          <h3 className="text-xl font-bold text-gray-900">Orders &amp; work orders</h3>
          {reportData.period && (
            <p className="text-sm text-gray-600">
              {reportData.period.from} — {reportData.period.to}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-2">Document types: {types}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {renderInsightCard({
            title: 'Documents',
            value: String(summary.document_count ?? documents.length),
            subtitle: 'In selected period',
            tone: 'blue',
          })}
          {renderInsightCard({
            title: 'Line rows',
            value: String(summary.total_lines ?? '—'),
            subtitle: 'BOM lines on documents',
            tone: 'purple',
          })}
          {renderInsightCard({
            title: 'Quantity on lines',
            value: String(summary.total_line_quantity ?? '—'),
            subtitle: 'Sum of line quantities',
            tone: 'green',
          })}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Lines</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Line qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {documents.map((row: any) => (
                  <tr key={`${row.item_number}-${row.document_date}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap">{row.document_date}</td>
                    <td className="px-4 py-2 capitalize">{row.item_type}</td>
                    <td className="px-4 py-2 font-mono text-xs">{row.item_number}</td>
                    <td className="px-4 py-2 max-w-xs truncate" title={row.title}>
                      {row.title}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{row.contact_name}</td>
                    <td className="px-4 py-2 text-center text-xs">{row.status}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(row.grand_total)}</td>
                    <td className="px-4 py-2 text-right">{row.line_count}</td>
                    <td className="px-4 py-2 text-right">{row.total_line_quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentRemindersSent = () => {
    if (!reportData) return null;
    const { summary, records } = reportData;

    const statusBadge = (status: string) => {
      switch (status) {
        case 'success':
        case 'sent':
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3" /> Sent
            </span>
          );
        case 'failed':
        case 'error':
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <XCircle className="w-3 h-3" /> Failed
            </span>
          );
        case 'pending':
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              <Clock className="w-3 h-3" /> Pending
            </span>
          );
        default:
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {status}
            </span>
          );
      }
    };

    const typeBadge = (type: string) => (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${type === 'immediate'
        ? 'bg-blue-100 text-blue-700'
        : 'bg-purple-100 text-purple-700'
        }`}>
        {type === 'immediate' ? <Send className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
        {type === 'immediate' ? 'Immediate' : 'Scheduled'}
      </span>
    );

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Payment Reminders Sent</h3>
          <p className="text-sm text-gray-600">
            {reportData.period?.from} — {reportData.period?.to}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900">{summary?.total_reminders ?? 0}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
            <p className="text-sm text-green-600">Sent</p>
            <p className="text-2xl font-bold text-green-700">{summary?.sent ?? 0}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200 text-center">
            <p className="text-sm text-red-600">Failed</p>
            <p className="text-2xl font-bold text-red-700">{summary?.failed ?? 0}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 text-center">
            <p className="text-sm text-yellow-600">Pending</p>
            <p className="text-2xl font-bold text-yellow-700">{summary?.pending ?? 0}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
            <p className="text-sm text-blue-600">Immediate</p>
            <p className="text-2xl font-bold text-blue-700">{summary?.immediate_reminders ?? 0}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 text-center">
            <p className="text-sm text-purple-600">Scheduled</p>
            <p className="text-2xl font-bold text-purple-700">{summary?.scheduled_reminders ?? 0}</p>
          </div>
        </div>

        {/* Records table */}
        {records && records.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {records.map((rec: any) => (
                    <tr key={rec.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {new Date(rec.date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        <br />
                        <span className="text-xs text-gray-500">
                          {formatTimeSAST(rec.date)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{typeBadge(rec.type)}</td>
                      <td className="px-4 py-3 text-sm">{statusBadge(rec.status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{rec.contact_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">{rec.invoice_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {(rec.recipients || []).map((e: string, i: number) => (
                          <div key={i} className="truncate max-w-[200px]" title={e}>{e}</div>
                        ))}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{rec.sent_by}</td>
                      <td className="px-4 py-3 text-sm text-red-600 max-w-[200px] truncate" title={rec.error || ''}>
                        {rec.error || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No payment reminders found for this period</p>
          </div>
        )}
      </div>
    );
  };

  // Determine which renderer to use based on report ID
  const renderReport = () => {
    if (!reportData) return null;

    switch (report.id) {
      case 'finance_profit_loss':
        return renderProfitLoss();
      case 'finance_balance_sheet':
        return renderBalanceSheet();
      case 'finance_aged_receivables':
        return renderAgedReceivables();
      case 'finance_aged_payables':
        return renderAgedPayables();
      case 'finance_cash_flow':
        return renderCashFlow();
      case 'finance_vat_report':
        return renderVatReport();
      case 'finance_customer_statement':
        return renderCustomerStatement();
      case 'finance_inventory_valuation':
        return renderInventoryValuation();
      case 'finance_stock_movement':
        return renderStockMovement();
      case 'finance_low_stock_report':
        return renderLowStockReport();
      case 'finance_warehouse_summary':
        return renderWarehouseSummary();
      case 'finance_product_profitability':
        return renderProductProfitability();
      case 'finance_payment_reminders_sent':
        return renderPaymentRemindersSent();
      case 'finance_bank_reconciliation':
        return renderBankReconciliation();
      case 'finance_inventory_order_pipeline':
        return renderInventoryOrderPipeline();
      default:
        return renderGenericTable();
    }
  };

  return (
    <div className="space-y-6">
      {/* Parameters Card */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <button
                onClick={onBack}
                className="p-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                title="Back to finance reports"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileBarChart className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  {report.name}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{report.description}</p>
              </div>
            </div>

            {reportData ? (
              <div className="flex items-center gap-2 rounded-lg border border-blue-100 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-3 py-2 text-sm text-blue-800 dark:text-blue-300 lg:ml-4">
                <ArrowUpRight className="h-4 w-4 flex-shrink-0" />
                <span>Use the header <span className="font-semibold">Print Preview</span> action for preview, PDF, CSV, and email.</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="px-6 py-5">
          {/* Parameters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {report.parameters.map(param => (
              <div key={param.name}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {param.label}
                  {param.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderParameterInput(param)}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <button
                onClick={executeReport}
                disabled={loading || !hasRequiredParameters}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Generate Report
              </button>
            </div>

            {!hasRequiredParameters ? (
              <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                Complete all required fields to generate this report.
              </p>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Generate the report, then use the header Print Preview action for preview, export, and email.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-red-900 dark:text-red-300">Error</h4>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Report Content */}
      {renderReport()}

      {/* Empty State */}
      {!loading && !reportData && !error && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Ready to generate report</h3>
          <p className="text-gray-600 dark:text-gray-400">Configure parameters above and click "Generate Report" to begin</p>
        </div>
      )}
    </div>
  );
}
